"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const bikes = [
{
  id: 1,
  name: "Apex Road Racer",
  category: "Road",
  price: "$2,499",
  originalPrice: "$2,799",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_145ed6a1a-1765020529932.png",
  alt: "Sleek red road bicycle with carbon frame and drop handlebars",
  tags: ["Carbon Frame", "22-Speed", "Disc Brakes"],
  badge: "Bestseller",
  badgeColor: "bg-amber text-charcoal",
  size: "large"
},
{
  id: 2,
  name: "Summit MTB Pro",
  category: "Mountain",
  price: "$3,199",
  originalPrice: null,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_170e7c032-1770577686848.png",
  alt: "Black full-suspension mountain bike on rocky dirt trail",
  tags: ["Full Suspension", "29\" Wheels", "Hydraulic Brakes"],
  badge: "New 2026",
  badgeColor: "bg-charcoal text-cream",
  size: "medium"
},
{
  id: 3,
  name: "Urban Glide 7",
  category: "City",
  price: "$899",
  originalPrice: "$1,099",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_183aebbd5-1767045447128.png",
  alt: "Silver city commuter bike with fenders and rear rack",
  tags: ["Lightweight", "7-Speed", "Fenders Included"],
  badge: "Best Value",
  badgeColor: "bg-green-600 text-white",
  size: "medium"
},
{
  id: 4,
  name: "E-Volt 500W",
  category: "Electric",
  price: "$4,199",
  originalPrice: null,
  image: "https://images.unsplash.com/photo-1669183013871-08908e0e222a",
  alt: "Yellow electric bicycle with integrated battery and LCD display",
  tags: ["500W Motor", "80km Range", "USB Charging"],
  badge: "Electric",
  badgeColor: "bg-blue-600 text-white",
  size: "large"
}];


export default function FeaturedBikes() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("hidden");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    const reveals = sectionRef?.current?.querySelectorAll(".reveal");
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="bikes" ref={sectionRef} className="py-24 px-6 bg-cream">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="reveal hidden">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-amber mb-3">
              2026 Collection
            </p>
            <h2 className="font-heading text-5xl md:text-6xl font-black leading-none tracking-tight text-charcoal">
              Featured{" "}
              <span className="italic font-light">Rides.</span>
            </h2>
          </div>
          <p className="text-stone-warm max-w-xs reveal hidden reveal-delay-2">
            Carefully selected bikes for every style of cycling — performance tested and rider-approved.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Large card - spans 2 rows on lg */}
          <div className="bike-card glass-card rounded-4xl overflow-hidden lg:row-span-2 reveal hidden">
            <div className="relative overflow-hidden" style={{ height: "340px" }}>
              <AppImage
                src={bikes?.[0]?.image}
                alt={bikes?.[0]?.alt}
                fill
                className="card-image object-cover" />
              
              <span className={`amber-badge absolute top-4 left-4 px-3 py-1 rounded-full ${bikes?.[0]?.badgeColor}`}>
                {bikes?.[0]?.badge}
              </span>
            </div>
            <div className="p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-stone-warm mb-1">{bikes?.[0]?.category}</p>
              <h3 className="font-heading text-2xl font-bold text-charcoal mb-3">{bikes?.[0]?.name}</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {bikes?.[0]?.tags?.map((tag) =>
                <span key={tag} className="text-xs bg-stone-border/50 text-charcoal px-2.5 py-1 rounded-full font-medium">
                    {tag}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-heading text-2xl font-bold text-charcoal">{bikes?.[0]?.price}</span>
                  {bikes?.[0]?.originalPrice &&
                  <span className="text-stone-warm line-through text-sm ml-2">{bikes?.[0]?.originalPrice}</span>
                  }
                </div>
                <button className="btn-amber w-10 h-10 rounded-full flex items-center justify-center" aria-label="Add to cart">
                  <Icon name="ShoppingCartIcon" size={18} className="text-charcoal" />
                </button>
              </div>
            </div>
          </div>

          {/* Medium cards */}
          {[bikes?.[1], bikes?.[2]]?.map((bike, i) =>
          <div key={bike?.id} className={`bike-card glass-card rounded-4xl overflow-hidden reveal hidden reveal-delay-${i + 1}`}>
              <div className="relative overflow-hidden" style={{ height: "200px" }}>
                <AppImage
                src={bike?.image}
                alt={bike?.alt}
                fill
                className="card-image object-cover" />
              
                <span className={`amber-badge absolute top-4 left-4 px-3 py-1 rounded-full ${bike?.badgeColor}`}>
                  {bike?.badge}
                </span>
              </div>
              <div className="p-5">
                <p className="text-xs font-bold uppercase tracking-widest text-stone-warm mb-1">{bike?.category}</p>
                <h3 className="font-heading text-xl font-bold text-charcoal mb-2">{bike?.name}</h3>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {bike?.tags?.map((tag) =>
                <span key={tag} className="text-xs bg-stone-border/50 text-charcoal px-2 py-0.5 rounded-full font-medium">
                      {tag}
                    </span>
                )}
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-heading text-xl font-bold text-charcoal">{bike?.price}</span>
                    {bike?.originalPrice &&
                  <span className="text-stone-warm line-through text-sm ml-2">{bike?.originalPrice}</span>
                  }
                  </div>
                  <button className="btn-amber w-9 h-9 rounded-full flex items-center justify-center" aria-label="Add to cart">
                    <Icon name="ShoppingCartIcon" size={16} className="text-charcoal" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Electric bike - large card */}
          <div className="bike-card glass-card rounded-4xl overflow-hidden lg:col-span-2 reveal hidden reveal-delay-3">
            <div className="flex flex-col md:flex-row">
              <div className="relative overflow-hidden md:w-1/2" style={{ height: "240px" }}>
                <AppImage
                  src={bikes?.[3]?.image}
                  alt={bikes?.[3]?.alt}
                  fill
                  className="card-image object-cover" />
                
                <span className={`amber-badge absolute top-4 left-4 px-3 py-1 rounded-full ${bikes?.[3]?.badgeColor}`}>
                  {bikes?.[3]?.badge}
                </span>
              </div>
              <div className="p-6 md:w-1/2 flex flex-col justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-stone-warm mb-1">{bikes?.[3]?.category}</p>
                  <h3 className="font-heading text-2xl font-bold text-charcoal mb-3">{bikes?.[3]?.name}</h3>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {bikes?.[3]?.tags?.map((tag) =>
                    <span key={tag} className="text-xs bg-stone-border/50 text-charcoal px-2.5 py-1 rounded-full font-medium">
                        {tag}
                      </span>
                    )}
                  </div>
                  <p className="text-stone-warm text-sm leading-relaxed">
                    Conquer hills and long commutes effortlessly. The E-Volt 500W integrates seamlessly into daily life with zero compromise on style.
                  </p>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div>
                    <span className="font-heading text-2xl font-bold text-charcoal">{bikes?.[3]?.price}</span>
                  </div>
                  <a href="#" className="btn-amber px-5 py-2.5 rounded-full text-sm flex items-center gap-2">
                    View Details
                    <Icon name="ArrowRightIcon" size={16} className="text-charcoal" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* View all CTA */}
        <div className="mt-12 text-center reveal hidden reveal-delay-4">
          <a
            href="#"
            className="btn-outline inline-flex items-center gap-2 px-10 py-4 rounded-full text-sm">
            
            View All 120+ Bikes
            <Icon name="ArrowRightIcon" size={16} />
          </a>
        </div>
      </div>
    </section>);

}